/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import { useState, useEffect } from 'react';

const THEORY_PRIMER_STORAGE_KEY = 'guituitive-theory-primer-dismissed';
const THEORY_CARD_STORAGE_KEY = 'guituitive-theory-card-dismissed';
const THEORY_PRIMER_VERSION = '1.0'; // Increment to show primer again for existing users

interface UseTheoryPrimerReturn {
  shouldShowTheoryPrimer: boolean;
  shouldShowTheoryCard: boolean;
  dismissTheoryPrimer: () => void;
  dismissTheoryCard: () => void;
  resetTheoryPrimer: () => void;
  showTheoryHints: () => void;
}

export const useTheoryPrimer = (): UseTheoryPrimerReturn => {
  const [shouldShowTheoryPrimer, setShouldShowTheoryPrimer] = useState(true);
  const [shouldShowTheoryCard, setShouldShowTheoryCard] = useState(true);

  useEffect(() => {
    const checkTheoryPrimerStatus = () => {
      try {
        const storedPrimer = localStorage.getItem(THEORY_PRIMER_STORAGE_KEY);
        const primerData = storedPrimer ? JSON.parse(storedPrimer) : null;

        // Show theory primer if:
        // 1. No data exists (first time user)
        // 2. Version has changed (we want to show updated content)
        const shouldShowPrimer = !primerData || primerData.version !== THEORY_PRIMER_VERSION;

        setShouldShowTheoryPrimer(shouldShowPrimer);

        // Check theory card status
        const storedCard = localStorage.getItem(THEORY_CARD_STORAGE_KEY);
        const cardData = storedCard ? JSON.parse(storedCard) : null;

        const shouldShowCard = !cardData || cardData.version !== THEORY_PRIMER_VERSION;

        setShouldShowTheoryCard(shouldShowCard);
      } catch (error) {
        console.warn('Error checking theory primer status:', error);
        // If there's an error reading storage, show primer to be safe
        setShouldShowTheoryPrimer(true);
        setShouldShowTheoryCard(true);
      }
    };

    checkTheoryPrimerStatus();
  }, []);

  const dismissTheoryPrimer = () => {
    try {
      const primerData = {
        dismissed: true,
        version: THEORY_PRIMER_VERSION,
        dismissedAt: new Date().toISOString()
      };

      localStorage.setItem(THEORY_PRIMER_STORAGE_KEY, JSON.stringify(primerData));
      setShouldShowTheoryPrimer(false);
    } catch (error) {
      console.warn('Error saving theory primer dismissal:', error);
      setShouldShowTheoryPrimer(false);
    }
  };

  const dismissTheoryCard = () => {
    try {
      const cardData = {
        dismissed: true,
        version: THEORY_PRIMER_VERSION,
        dismissedAt: new Date().toISOString()
      };

      localStorage.setItem(THEORY_CARD_STORAGE_KEY, JSON.stringify(cardData));
      setShouldShowTheoryCard(false);
    } catch (error) {
      console.warn('Error saving theory card dismissal:', error);
      setShouldShowTheoryCard(false);
    }
  };

  const resetTheoryPrimer = () => {
    try {
      localStorage.removeItem(THEORY_PRIMER_STORAGE_KEY);
      localStorage.removeItem(THEORY_CARD_STORAGE_KEY);
      setShouldShowTheoryPrimer(true);
      setShouldShowTheoryCard(true);
    } catch (error) {
      console.warn('Error resetting theory primer status:', error);
    }
  };

  const showTheoryHints = () => {
    // Temporarily show the hints without removing from localStorage
    // This way they can be dismissed again and won't auto-show on next visit
    setShouldShowTheoryPrimer(true);
    setShouldShowTheoryCard(true);
  };

  return {
    shouldShowTheoryPrimer,
    shouldShowTheoryCard,
    dismissTheoryPrimer,
    dismissTheoryCard,
    resetTheoryPrimer,
    showTheoryHints
  };
};

export default useTheoryPrimer;
